import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';
import _ from 'underscore';

import {bulkArchiveAssaySchemas, bulkUnarchiveAssaySchemas} from 'archive/ArchiveActions';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import useAreOrganizationSchemasCreatableQuery from 'auth/useAreOrganizationSchemasCreatableQuery';
import * as benchlingUtil from 'BenchlingUtil';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {AssaySchemasPageQuery} from 'results/assay-schemas/__generated/AssaySchemasPageQuery';
import {AssaySchemasPageWithoutFieldsQuery} from 'results/assay-schemas/__generated/AssaySchemasPageWithoutFieldsQuery';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import SchemasListing, {SchemasListingSchema} from 'schemas/SchemasListing';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {assertNever} from 'util/Types';

import {ASSAY_SCHEMA_TYPE_NAMES, ASSAY_TYPES} from './AssaySchemaConstants';
import * as AssaySchemaDAO from './AssaySchemaDAO';

export type AssaySchemaListingWithoutFields = SchemasListingSchema & {schemaType: ASSAY_TYPES};
export type AssaySchemaListing = AssaySchemaListingWithoutFields & {fieldsJson: Array<SchemaField>};

interface PresentationalAssaySchemasPageProps {
  schemaType: ASSAY_TYPES;
  listings: Array<AssaySchemaListingWithoutFields> | Array<AssaySchemaListing> | null;
  areOrganizationSchemasCreatable: boolean;
  displayFields: boolean;
}

function getSchemaSpan(listing: AssaySchemaListingWithoutFields): JSX.Element {
  return (
    <span className="u-small-spacing-left">
      {listing.name}
      <ArchiveRecordLabel item={listing} />
    </span>
  );
}

function mapSchemaWithoutFieldsToRow(listing: AssaySchemaListingWithoutFields): Array<JSX.Element> {
  return [getSchemaSpan(listing), <HorizontalThrobber />];
}

function mapSchemaWithFieldsToRow(listing: AssaySchemaListing): Array<JSX.Element | string> {
  return [getSchemaSpan(listing), listing.fieldsJson.map((field) => field.displayName).join(', ')];
}

export class PresentationalAssaySchemasPage extends React.Component<PresentationalAssaySchemasPageProps> {
  private handleNavigateToCreate = (): void => {
    benchlingUtil.navigate(null, AssaySchemaDAO.createAssaySchemaURL(this.props.schemaType));
  };

  render(): JSX.Element {
    benchlingUtil.setWindowTitle(`${ASSAY_SCHEMA_TYPE_NAMES[this.props.schemaType]} Schemas`);
    return (
      <SchemasListing
        columnHeaders={[
          {name: 'Schemas', width: 4},
          {name: 'Fields', width: 8},
        ]}
        schemas={this.props.listings}
        schemaType={this.props.schemaType}
        navigateToCreate={this.handleNavigateToCreate}
        urlForSchema={(schemaId: string) => AssaySchemaDAO.assaySchemaURL(this.props.schemaType, schemaId)}
        archiveSchemas={bulkArchiveAssaySchemas}
        unarchiveSchemas={bulkUnarchiveAssaySchemas}
        isWritable={this.props.areOrganizationSchemasCreatable}
        mapSchemaToRowValue={
          this.props.displayFields ? mapSchemaWithFieldsToRow : mapSchemaWithoutFieldsToRow
        }
      />
    );
  }
}

interface AssaySchemasPageProps {
  schemaType: ASSAY_TYPES;
}

export const ASSAY_SCHEMAS_PAGE_WITHOUT_FIELDS_QUERY: DocumentNode = gql`
  query AssaySchemasPageWithoutFieldsQuery @skipAudit @engComponent(name: RESULTS) {
    requestSchemas {
      ... on RequestSchemaNode {
        id
        name
        schemaType
      }
      ...HasArchiveRecordGraphQL
    }
    resultSchemas {
      ... on ResultSchemaNode {
        id
        name
        schemaType
      }
      ...HasArchiveRecordGraphQL
    }
    runSchemas {
      ... on RunSchemaNode {
        id
        name
        schemaType
      }
      ...HasArchiveRecordGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
`;

export const ASSAY_SCHEMAS_PAGE_QUERY: DocumentNode = gql`
  query AssaySchemasPageQuery @skipAudit @engComponent(name: RESULTS) {
    requestSchemas {
      ... on RequestSchemaNode {
        id
        name
        schemaType
        fieldsJson
      }
      ...HasArchiveRecordGraphQL
    }
    resultSchemas {
      ... on ResultSchemaNode {
        id
        name
        schemaType
        fieldsJson
      }
      ...HasArchiveRecordGraphQL
    }
    runSchemas {
      ... on RunSchemaNode {
        id
        name
        schemaType
        fieldsJson
      }
      ...HasArchiveRecordGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
`;

function getQueryFieldFromSchemaType(schemaType: LegacyAssaySchemaType): keyof AssaySchemasPageQuery {
  switch (schemaType) {
    case LegacyAssaySchemaType.ASSAY_REQUEST:
      return 'requestSchemas';
    case LegacyAssaySchemaType.ASSAY_RESULT:
      return 'resultSchemas';
    case LegacyAssaySchemaType.ASSAY_RUN:
      return 'runSchemas';
    default:
      assertNever(schemaType);
      return 'runSchemas';
  }
}

function sortAssaySchemas<T extends SchemasListingSchema>(assaySchemas: Array<T>): Array<T> {
  return _.sortBy(assaySchemas, ({name}) => name.toLowerCase());
}

export default function AssaySchemasPage(props: AssaySchemasPageProps): JSX.Element {
  const areOrganizationSchemasCreatableResults = useAreOrganizationSchemasCreatableQuery();
  const areOrganizationSchemasCreatable =
    areOrganizationSchemasCreatableResults.queryResultType === QueryResultType.SUCCESS &&
    areOrganizationSchemasCreatableResults.areOrganizationSchemasCreatable;
  const pageWithoutFieldsQuery = useBenchlingQuery<AssaySchemasPageWithoutFieldsQuery>(
    ASSAY_SCHEMAS_PAGE_WITHOUT_FIELDS_QUERY
  );
  const queryField = getQueryFieldFromSchemaType(props.schemaType);
  const loadedPage = pageWithoutFieldsQuery.type === QueryResultType.SUCCESS;
  const listingsWithoutFields = loadedPage
    ? sortAssaySchemas(pageWithoutFieldsQuery.data[queryField] as Array<AssaySchemaListingWithoutFields>)
    : null;
  const pageWithFieldsQuery = useBenchlingQuery<AssaySchemasPageQuery>(ASSAY_SCHEMAS_PAGE_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const loadedFields = pageWithFieldsQuery.type === QueryResultType.SUCCESS;
  const listings = loadedFields
    ? sortAssaySchemas(pageWithFieldsQuery.data[queryField] as Array<AssaySchemaListing>)
    : listingsWithoutFields;
  return (
    <PresentationalAssaySchemasPage
      {...props}
      listings={listings}
      areOrganizationSchemasCreatable={areOrganizationSchemasCreatable}
      displayFields={loadedFields}
    />
  );
}
